/**
 * User Login History Modal
 *
 * Admin modal component that displays comprehensive authentication history for a specific user.
 * Shows login attempts (success/failure), magic link generation events, and diagnostic information
 * in a chronological timeline view.
 */

import React from "react";
import styled from "styled-components";
import { Button } from "@flexport/latitude-typescript";

// Login history entry from GraphQL
interface LoginHistoryEntry {
  id: string;
  timestamp: string;
  eventType: string;
  success: boolean;
  ipAddress: string | null;
  userAgent: string | null;
  failureReason: string | null;
  generatedBy: string | null;
  tokenExpiry: string | null;
  messageId: string | null;
}

// User info from GraphQL
interface UserInfo {
  id: string;
  name: string | null;
  email: string;
  role: string;
}

// Modal component props
interface UserLoginHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserInfo | null;
  entries: LoginHistoryEntry[];
  loading: boolean;
  error: string | null;
}

// Styled components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 1000px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  color: #666;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;

  &:hover {
    color: #333;
  }
`;

const UserInfoSection = styled.div`
  font-size: 14px;
  color: #666;
  margin-top: 4px;
`;

const Content = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const Timeline = styled.div`
  position: relative;
  padding-left: 40px;
`;

const TimelineItem = styled.div<{ success: boolean }>`
  position: relative;
  padding-bottom: 24px;

  &::before {
    content: "";
    position: absolute;
    left: -32px;
    top: 8px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${(props) => (props.success ? "#52c41a" : "#ff4d4f")};
    border: 2px solid white;
    box-shadow: 0 0 0 2px ${(props) => (props.success ? "#52c41a" : "#ff4d4f")};
  }

  &::after {
    content: "";
    position: absolute;
    left: -26px;
    top: 20px;
    width: 2px;
    height: calc(100% - 12px);
    background: #e0e0e0;
  }

  &:last-child::after {
    display: none;
  }
`;

const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const EventType = styled.div<{ type: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${(props) => {
    switch (props.type) {
      case "login_success":
        return "#d4edda";
      case "login_failed":
        return "#f8d7da";
      case "magic_link_generated":
      case "magic_link_sent":
        return "#d1ecf1";
      default:
        return "#e2e3e5";
    }
  }};
  color: ${(props) => {
    switch (props.type) {
      case "login_success":
        return "#155724";
      case "login_failed":
        return "#721c24";
      case "magic_link_generated":
      case "magic_link_sent":
        return "#0c5460";
      default:
        return "#383d41";
    }
  }};
`;

const Timestamp = styled.div`
  font-size: 13px;
  color: #888;
`;

const EventDetails = styled.div`
  background: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  font-size: 13px;
  color: #555;
`;

const DetailRow = styled.div`
  display: flex;
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  min-width: 140px;
  color: #333;
`;

const DetailValue = styled.span`
  color: #555;
  word-break: break-all;
`;

const FailureReason = styled.div`
  background: #fff3cd;
  border-left: 4px solid #ffc107;
  padding: 8px 12px;
  margin-top: 8px;
  border-radius: 4px;
  font-size: 13px;
  color: #856404;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const ErrorState = styled.div`
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  padding: 16px;
  color: #721c24;
  text-align: center;
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
`;

/**
 * Format event type for display
 */
const formatEventType = (type: string): string => {
  switch (type) {
    case "login_success":
      return "Login Success";
    case "login_failed":
      return "Login Failed";
    case "magic_link_generated":
      return "Magic Link Generated";
    case "magic_link_sent":
      return "Magic Link Sent";
    default:
      return type;
  }
};

/**
 * Format timestamp for display
 */
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });
};

/**
 * User Login History Modal Component
 */
const UserLoginHistoryModal: React.FC<UserLoginHistoryModalProps> = ({
  isOpen,
  onClose,
  user,
  entries,
  loading,
  error,
}) => {
  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <div>
            <Title>Login History</Title>
            {user && (
              <UserInfoSection>
                {user.name || "Unknown"} ({user.email}) - {user.role}
              </UserInfoSection>
            )}
          </div>
          <CloseButton onClick={onClose} aria-label="Close">
            ×
          </CloseButton>
        </Header>

        <Content>
          {loading && (
            <LoadingState>
              <p>Loading login history...</p>
            </LoadingState>
          )}

          {error && (
            <ErrorState>
              <strong>Error:</strong> {error}
            </ErrorState>
          )}

          {!loading && !error && entries.length === 0 && (
            <EmptyState>
              <p>No login history found for this user.</p>
            </EmptyState>
          )}

          {!loading && !error && entries.length > 0 && (
            <Timeline>
              {entries.map((entry) => (
                <TimelineItem key={entry.id} success={entry.success}>
                  <EventHeader>
                    <EventType type={entry.eventType}>
                      {formatEventType(entry.eventType)}
                    </EventType>
                    <Timestamp>{formatTimestamp(entry.timestamp)}</Timestamp>
                  </EventHeader>

                  <EventDetails>
                    {entry.ipAddress && (
                      <DetailRow>
                        <DetailLabel>IP Address:</DetailLabel>
                        <DetailValue>{entry.ipAddress}</DetailValue>
                      </DetailRow>
                    )}

                    {entry.generatedBy && (
                      <DetailRow>
                        <DetailLabel>Generated By:</DetailLabel>
                        <DetailValue>{entry.generatedBy}</DetailValue>
                      </DetailRow>
                    )}

                    {entry.tokenExpiry && (
                      <DetailRow>
                        <DetailLabel>Link Expiry:</DetailLabel>
                        <DetailValue>
                          {formatTimestamp(entry.tokenExpiry)}
                        </DetailValue>
                      </DetailRow>
                    )}

                    {entry.messageId && (
                      <DetailRow>
                        <DetailLabel>Email Message ID:</DetailLabel>
                        <DetailValue>{entry.messageId}</DetailValue>
                      </DetailRow>
                    )}

                    {entry.userAgent && (
                      <DetailRow>
                        <DetailLabel>User Agent:</DetailLabel>
                        <DetailValue>{entry.userAgent}</DetailValue>
                      </DetailRow>
                    )}
                  </EventDetails>

                  {entry.failureReason && (
                    <FailureReason>
                      <strong>Failure Reason:</strong>{" "}
                      {entry.failureReason.replace(/_/g, " ")}
                    </FailureReason>
                  )}
                </TimelineItem>
              ))}
            </Timeline>
          )}
        </Content>

        <Footer>
          <Button onClick={onClose} kind="hollow">
            Close
          </Button>
        </Footer>
      </ModalContainer>
    </Overlay>
  );
};

export default UserLoginHistoryModal;
